//
// SchemaTreeStore
//
// The SchemaTreeStore handles interaction with the Definition Navigation Tree
// (TreeStore)
//
// The exposed store is the actual TreeStore (.tree), but that is a generic element
// and the SchemaTreeStore handles storing view choices like expandedItems and such,
// and fetching data based on that.
//
// In a store, we use it like this:
//
//     this._schematree = new SchemaTreeStore(this._rootstore)
//      ...
//     get schematree() {
//         if (!this.user && !this.project) return null
//         this._schematree.loadOnce()
//         return this._schematree.tree // NOTE the .tree!
//     }
//      ...
//     _schematree is observable, schematree is computed
//
// Have a look at the TreeNavigator component to see how it is used in rendering.
//
// SIDE-EFFECT: when a user selects a treeitem, we update the schemaworksheet with that
//              same item

import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from 'mobx'

import { TreeStore, TreeStoreItem } from '../components/TreeStore'
import { ClassData } from '../data/ClassData'
import { DefinitionData } from '../data/DefinitionData'
import { FieldData } from '../data/FieldData'
import { sortItemsNewestFirst } from '../data/utils'

export const sortSchemaTreeItems = (language, labelView) => {
    const sort = sortItemsNewestFirst(language, labelView)
    const _sort = (a, b) => {
        return sort(a.item, b.item)
    }
    return _sort
}

export class SchemaTreeStore {
    __init = false
    _key = null
    _rootstore = null
    tree = null

    constructor(key, rootstore) {
        makeObservable(this, {
            __init: observable,
            _key: observable,
            _rootstore: observable,
            language: computed,
            tree: observable,
            fetch: action.bound,
            refetch: action.bound,
            _fetch: action.bound,
            select: action.bound,
            selectItem: action.bound,
            setItem: action.bound,
            syncdataitem_callback: action.bound,
        })

        this._key = key
        this._rootstore = rootstore
        this.tree = new TreeStore(this)

        this._syncdebounce = null
        this._syncdelay = 100
    }

    loadOnce = () => {
        if (this.__init) return
        const parentIds = this._rootstore.view.loadPerProject(
            this._key + '/expandedIds',
            []
        )
        const selectedId = this._rootstore.view.loadPerProject(
            this._key + '/selectedId'
        )
        this.tree._expandedIds.replace(parentIds)
        this._fetch(parentIds).then(() => {
            runInAction(() => {
                this.tree._selectedId = selectedId
                this.tree.multiselection.rangeStart(selectedId)
            })
            reaction(
                () => ({
                    selectedGid: this.tree._selectedId,
                    selectionsize: this.tree.multiselection.size,
                }),
                ({ selectedGid, selectionsize }) => {
                    if (selectedGid && !selectionsize) {
                        this.tree.multiselection.rangeStart(selectedGid)
                    }
                }
            )
            reaction(
                () => this._rootstore.view._environment.get('language'),
                language => {
                    this._fetch(this.tree._expandedIds)
                }
            )
            reaction(
                () => this._rootstore.view._environment.get('labelView'),
                labelView => {
                    this._fetch(this.tree._expandedIds)
                }
            )
        })
        this._rootstore.api.register_syncdataitem_callback(this.syncdataitem_callback)
        this.__init = true
    }

    fetch(parentIds) {
        this._rootstore.view.savePerProject(this._key + '/expandedIds', parentIds)
        return this._fetch(parentIds)
    }

    refetch() {
        return this._fetch(this.tree._expandedIds)
    }

    get language() {
        if (!this._rootstore.view.environment) return null
        return this._rootstore.view.environment.get('language')
    }

    _getOriginalGidFromDefinition(definition) {
        // this returns the tree id, which should be an original
        return definition.is_working_copy && !definition.is_new
            ? definition.original
            : definition.gid
    }

    _getSubtreeList(parentItem, definitions, parentIds, path) {
        let treeList = []

        const definition = definitions.get(
            this._getOriginalGidFromDefinition(parentItem.item)
        )
        if (definition) {
            definition.childdefinitions.forEach(childgid => {
                const childdefinition = definitions.get(childgid)
                if (!childdefinition) return
                const childItem = new TreeStoreItem(
                    this._getOriginalGidFromDefinition(childdefinition),
                    parentItem.id,
                    parentItem.depth + 1,
                    childdefinition.childdefinitions.length,
                    childdefinition,
                    { is_link: false }
                )
                treeList.push(childItem)
                if (
                    childdefinition.childdefinitions.length &&
                    parentIds.includes(childItem.id) &&
                    !path.includes(childItem.item.gid)
                ) {
                    treeList.push(
                        ...this._getSubtreeList(childItem, definitions, parentIds, [
                            ...path,
                            childItem.item.gid,
                        ])
                    )
                }
            })
        }
        return treeList
    }

    getDataTypeForGid(gid) {
        if (this._rootstore.data.definitions.has(gid)) {
            return 'definition'
        } else if (this._rootstore.data.classes.has(gid)) {
            return 'class'
        } else if (this._rootstore.data.fields.has(gid)) {
            return 'field'
        }
        return null
    }

    getTreeIdsForDefinitionGid(definitionGid) {
        if (!definitionGid) return []
        let toBeResolved = new Set([definitionGid])
        let resolved = new Set()
        let parentIds = new Set()

        const definitions = this._rootstore.data.definitions.values()

        const modifiedGids = new Set()
        const definitionsView = definitions
            .filter(definition => {
                if (definition.is_working_copy) {
                    modifiedGids.add(definition.original)
                }

                return (
                    definition.is_committed ||
                    definition.is_new ||
                    definition.is_working_copy
                )
            })
            .filter(definition => {
                return !modifiedGids.has(definition.gid)
            })

        const parentMap = new Map()
        for (const definition of definitionsView) {
            const definitionOriginalGid = this._getOriginalGidFromDefinition(definition)
            for (const childGid of definition.childdefinitions) {
                if (!parentMap.has(childGid)) {
                    parentMap.set(childGid, new Set())
                }
                parentMap.get(childGid).add(definitionOriginalGid)
            }
        }

        while (toBeResolved.size > 0) {
            for (let gid of toBeResolved) {
                resolved.add(gid)
                if (!parentMap.has(gid)) {
                    continue
                }
                parentIds = parentIds.union(parentMap.get(gid))
            }
            toBeResolved = parentIds.difference(resolved)
        }

        return ['definition', ...parentIds]
    }

    getTreeListFromDefinitions(definitions, parentIds) {
        // from a dict of defs, build an ordered list of Treeitems (gid, parentgid, indent)
        // first i need to find the roots and order them alphabetically
        // then for each root, create a tree and add those as treeitems
        //
        // definitions is not the complete map, only those that are not extended, and the
        // modified versions if available - mapped by original first, then gid
        const _sort = sortSchemaTreeItems(
            this.language,
            this._rootstore.view.environment.get('labelView')
        )
        const childDefinitionGids = new Set(
            definitions
                .values()
                .flatMap(definition =>
                    definition.childdefinitions.filter(
                        childGid =>
                            definition.gid !== childGid &&
                            definition.original !== childGid
                    )
                )
        )

        const roots = definitions
            .values()
            .filter(
                definition =>
                    !childDefinitionGids.has(
                        this._getOriginalGidFromDefinition(definition)
                    )
            )
            .map(
                definition =>
                    new TreeStoreItem(
                        this._getOriginalGidFromDefinition(definition),
                        'definition',
                        1,
                        definition.childdefinitions.length,
                        definition,
                        { is_link: false }
                    )
            )
            .toArray()
            .sort(_sort)

        // Build tree list
        const treeList = roots.flatMap(root => [
            root,
            ...(root.item.childdefinitions.length && parentIds.includes(root.id)
                ? this._getSubtreeList(root, definitions, parentIds, [root.item.gid])
                : []),
        ])

        // Mark links
        let usedDefs = new Set()
        treeList.forEach(treeItem => {
            if (!usedDefs.has(treeItem.id)) {
                usedDefs.add(treeItem.id)
            } else {
                treeItem.data.is_link = true
            }
        })

        return treeList
    }

    _fetch(parentIds) {
        const _sort = sortItemsNewestFirst(
            this.language,
            this._rootstore.view.environment.get('labelView')
        )
        let treeItems = []
        treeItems.push(
            new TreeStoreItem(
                'definition',
                'root',
                0,
                this._rootstore.data.definitions.size,
                'definitions'
            )
        )
        const definitions = this._rootstore.data.definitions.values().toArray()
        const modifiedGids = new Set()
        const definitionTreeMap = definitions
            .filter(definition => {
                if (definition.is_working_copy) {
                    modifiedGids.add(definition.original)
                }
                return (
                    definition.is_committed ||
                    definition.is_new ||
                    definition.is_working_copy
                )
            })
            // Skip definitions that have a working copy
            .filter(definition => !modifiedGids.has(definition.gid))
            .reduce((map, definition) => {
                map.set(this._getOriginalGidFromDefinition(definition), definition)
                return map
            }, new Map())

        if (!parentIds.length && !this.__init) {
            // no prior interaction ? make sure definitions all expand
            parentIds.push(...definitionTreeMap.keys())
        }

        if (parentIds.includes('definition')) {
            const definitionTreeList = this.getTreeListFromDefinitions(
                definitionTreeMap,
                parentIds
            )
            definitionTreeList.forEach(treeitem => {
                treeItems.push(treeitem)
            })
        }
        treeItems.push(
            new TreeStoreItem(
                'class',
                'root',
                0,
                this._rootstore.data.classes.size,
                'classes'
            )
        )
        if (parentIds.includes('class')) {
            const classes = this._rootstore.data.classes
            const modifiedGids = new Set(
                classes
                    .values()
                    .filter(class_ => class_.is_working_copy)
                    .map(class_ => class_.original)
            )
            treeItems.push(
                ...classes
                    .values()
                    .filter(class_ => !modifiedGids.has(class_.gid))
                    .toArray()
                    .sort(_sort)
                    .map(class_ => {
                        return new TreeStoreItem(class_.gid, 'class', 1, 0, class_)
                    })
            )
        }
        treeItems.push(
            new TreeStoreItem(
                'field',
                'root',
                0,
                this._rootstore.data.fields.size,
                'fields'
            )
        )
        if (parentIds.includes('field')) {
            const fields = this._rootstore.data.fields
            const modifiedGids = new Set(
                fields
                    .values()
                    .filter(field => field.is_working_copy)
                    .map(field => field.original)
            )
            treeItems.push(
                ...fields
                    .values()
                    .filter(field => !modifiedGids.has(field.gid))
                    .toArray()
                    .sort(_sort)
                    .map(field => {
                        return new TreeStoreItem(field.gid, 'field', 1, 0, field)
                    })
            )
        }

        this.tree._expandedIds.replace(parentIds)
        this.tree.setTreeItems(treeItems, treeItems.length)
        return Promise.resolve(true)
    }

    // Called from specific *TreeStore
    deselect() {
        this._rootstore.view.savePerProject(this._key + '/selectedId', null)
        this.selectItem(null)
    }

    select(treeitem) {
        if (treeitem) {
            this._rootstore.view.savePerProject(this._key + '/selectedId', treeitem.id)
            this.selectItem(treeitem.item)
        }
    }

    selectItem(item) {
        this._rootstore.view.schemaworksheet.setItem(item)
    }

    collapseToSelected() {
        const selectedId = this.tree._selectedId
        if (selectedId && !['definition', 'class', 'field'].includes(selectedId)) {
            const dataType = this.getDataTypeForGid(selectedId)
            const treeIds =
                dataType === 'definition'
                    ? this.getTreeIdsForDefinitionGid(selectedId)
                    : []
            const parentIds = [...new Set([dataType, selectedId, ...treeIds])]
            return this.fetch(parentIds)
        } else {
            return this.collapseAll()
        }
    }

    collapseAll() {
        return this.tree._setExpandedIds(['root'])
    }

    // Called when loaded from store
    setItem(item) {
        // this is external; when we select an item, we need to make sure it appears in
        // the expanded tree, and that it will be the selected treeitem
        const parentName =
            item instanceof DefinitionData
                ? 'definition'
                : item instanceof ClassData
                  ? 'class'
                  : item instanceof FieldData
                    ? 'field'
                    : 'root'

        const newExpandedIds = Array.from(
            new Set([...this.tree._expandedIds, parentName])
        )
        this.tree._setExpandedIds(newExpandedIds)
        this.tree.multiselection.deselectAll()

        if (typeof item !== 'string') {
            if (parentName === 'definition') {
                this.tree.setSelectedId(
                    item ? this._getOriginalGidFromDefinition(item) : null
                )
            } else {
                this.tree.setSelectedId(item ? item.gid : null)
            }

            if (item) {
                this.tree.multiselection.rangeStart(item.gid)
            }
        }
    }

    schedule_refetch = () => {
        if (this._syncdebounce) {
            clearTimeout(this._syncdebounce)
            this._syncdebounce = null
        }
        this._syncdebounce = setTimeout(() => {
            this._syncdebounce = null
            this.refetch()
        }, this._syncdelay)
    }

    syncdataitem_callback = (syncdataitem, data_before, data_after) => {
        // console.log('SchemaTreeStore sync', syncdataitem, data_before, data_after)
        // we're only interested in schema data_types
        // then, we're only interested in all additions and deletions, plus definition
        // updates
        if (!['definitions', 'classes', 'fields'].includes(syncdataitem['data_type'])) {
            return
        }
        if (syncdataitem['data_type'] === 'definitions') {
            const data = data_after ? data_after : data_before ? data_before : null
            if (!data) return
            // don't update for extended definitions
            if (data && data.is_extended) {
                return
            }
        }
        if (
            syncdataitem['data_type'] !== 'definitions' &&
            !['INSERT', 'DELETE'].includes(syncdataitem['action'])
        ) {
            return
        }
        this.schedule_refetch()
    }
}
